import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Button, Spinner } from 'react-bootstrap';
import { Menu, Save } from 'lucide-react';
import CountyMunicipalityModal from '../CountyMunicipality/CountyMunicipalityModal';
import CityContacts from '../CityContacts/CityContacts';
import useMunicipalityCounty from '../../hooks/useMunicipalityCounty';
import useIsMobile from '../../hooks/useIsMobile';
import { TurnaroundTimesContent } from '../Turnaround/TurnaroundTimes';
import TextSkeleton from '../TextSkeleton';
import BackOfficeSidebar from './BackOfficeSidebar';
import { BACK_OFFICE_TAB_HEADINGS, DEFAULT_BACK_OFFICE_ITEMS, MOBILE_BREAKPOINT } from '../../helpers/constants';
import InstructionBuilder from '../admin/InstructionBuilder/InstructionBuilder';
import HoaDirectoryPanel from '../HoaDirectory/HoaDirectoryPanel';
import LienSearchQaRules from './LienSearchQaRules';
import QualiaMessageTemplatesPanel from './QualiaMessageTemplatesPanel';

const LOCATION_SELECT_TAB_IDS = new Set([
  'contacts-city-department',
  'contacts-county-department',
  'contacts-provider',
  'turnaround-times'
]);

const QA_RULES_TAB_IDS = new Set(['lien-search-qa-rules', 'estoppel-qa-rules']);

const BackOffice = () => {
  const [countyMunicipalityModalOpen, setCountyMunicipalityModalOpen] = useState(false);
  const [activeItemId, setActiveItemId] = useState(DEFAULT_BACK_OFFICE_ITEMS[0]?.id || 'processing-instructions');
  const [selectedMunicipality, setSelectedMunicipality] = useState({
    'contacts-city-department': '',
    'contacts-provider': ''
  });
  const [selectedCounty, setSelectedCounty] = useState({
    'contacts-county-department': '',
    'turnaround-times': ''
  });
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeTabItemsMetadata, setActiveTabItemsMetadata] = useState({
    count: 0,
    loading: false
  });
  const instructionsRef = useRef(null);
  const qaRulesRef = useRef(null);
  const { municipalityOptions, records: countyMunicipalityRecords } = useMunicipalityCounty();

  const countyOptions = useMemo(() => {
    const uniqueCounties = new Set((countyMunicipalityRecords || []).map(item => item?.county).filter(Boolean));
    return Array.from(uniqueCounties)
      .sort((a, b) => a.localeCompare(b))
      .map(county => ({ label: county, value: county }));
  }, [countyMunicipalityRecords]);

  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile]);

  const openCountyMunicipalityModal = useCallback(() => setCountyMunicipalityModalOpen(true), []);

  const handleMunicipalitySelect = useCallback(option => {
    setSelectedMunicipality(prev => ({ ...prev, [activeItemId]: option?.value || '' }));
  }, [activeItemId]);

  const handleCountySelect = useCallback(option => {
    setSelectedCounty(prev => ({ ...prev, [activeItemId]: option?.value || '' }));
  }, [activeItemId]);

  const canChangeSidebarItem = useCallback(
    nextId => {
      if (nextId === activeItemId) return false;

      if (activeItemId === 'processing-instructions' && instructionsRef.current?.requestBackOfficeTabChange) {
        return instructionsRef.current.requestBackOfficeTabChange(nextId);
      }

      if (QA_RULES_TAB_IDS.has(activeItemId) && qaRulesRef.current?.requestBackOfficeTabChange) {
        return qaRulesRef.current.requestBackOfficeTabChange(nextId);
      }

      return false;
    },
    [activeItemId]
  );

  const heading = BACK_OFFICE_TAB_HEADINGS[activeItemId] || BACK_OFFICE_TAB_HEADINGS['processing-instructions'];

  const panel = useMemo(() => {
    switch (activeItemId) {
      case 'processing-instructions':
        return <InstructionBuilder ref={instructionsRef} onNavigateToTab={setActiveItemId} />;
      case 'contacts-city-department':
        return (
          <CityContacts
            syncedTab="City Department"
            city={selectedMunicipality['contacts-city-department'] || ''}
            setActiveTabItemsMetadata={setActiveTabItemsMetadata}
          />
        );
      case 'contacts-county-department':
        return (
          <CityContacts
            syncedTab="County Department"
            state={selectedCounty['contacts-county-department'] || ''}
            setActiveTabItemsMetadata={setActiveTabItemsMetadata}
          />
        );
      case 'contacts-provider':
        return (
          <CityContacts
            syncedTab="Provider"
            city={selectedMunicipality['contacts-provider'] || ''}
            setActiveTabItemsMetadata={setActiveTabItemsMetadata}
          />
        );
      case 'lien-search-qa-rules':
        return (
          <LienSearchQaRules
            ref={qaRulesRef}
            reportType="Lien Search"
            setActiveTabItemsMetadata={setActiveTabItemsMetadata}
            onNavigateToTab={setActiveItemId}
          />
        );
      case 'estoppel-qa-rules':
        return (
          <LienSearchQaRules
            ref={qaRulesRef}
            reportType="Estoppel"
            setActiveTabItemsMetadata={setActiveTabItemsMetadata}
            onNavigateToTab={setActiveItemId}
          />
        );
      case 'turnaround-times':
        return (
          <TurnaroundTimesContent
            embeddedInBackOffice
            setActiveTabItemsMetadata={setActiveTabItemsMetadata}
            county={selectedCounty['turnaround-times'] || ''}
          />
        );
      case 'hoa-directory':
        return (
          <HoaDirectoryPanel
            setActiveTabItemsMetadata={setActiveTabItemsMetadata}
          />
        );
      case 'qualia-message-templates':
        return (
          <QualiaMessageTemplatesPanel setActiveTabItemsMetadata={setActiveTabItemsMetadata} />
        );
      default:
        return <InstructionBuilder ref={instructionsRef} onNavigateToTab={setActiveItemId} />;
    }
  }, [activeItemId, selectedMunicipality, selectedCounty]);

  const showLocationSelect = LOCATION_SELECT_TAB_IDS.has(activeItemId);
  const isCountyDepartmentTab = activeItemId === 'contacts-county-department' || activeItemId === 'turnaround-times';
  const locationSelectPlaceholder = isCountyDepartmentTab ? 'Select county' : 'Select municipality';
  const locationSelectOptions = isCountyDepartmentTab ? countyOptions : municipalityOptions;
  const locationSelectValue = isCountyDepartmentTab
    ? countyOptions.find(option => option.value === (selectedCounty[activeItemId] || '')) || null
    : municipalityOptions.find(option => option.value === (selectedMunicipality[activeItemId] || '')) || null;
  const handleLocationSelect = isCountyDepartmentTab ? handleCountySelect : handleMunicipalitySelect;

  return (
    <>
      <BackOfficeSidebar
        openCountyMunicipalityModal={openCountyMunicipalityModal}
        activeItemId={activeItemId}
        setActiveItemId={setActiveItemId}
        onBeforeItemSelect={canChangeSidebarItem}
        isMobile={isMobile}
        mobileOpen={mobileSidebarOpen}
        onRequestClose={() => setMobileSidebarOpen(false)}
      >
        <>
          <div className="container-fluid flex-shrink-0 bg-white pb-1 pt-3 px-md-3">
            <div
              className="d-flex flex-wrap justify-content-between align-items-center gap-3"
              style={{
                paddingLeft: isMobile ? 0 : 24,
                paddingRight: isMobile ? 0 : 24
              }}
            >
              <div className="min-w-0 flex-grow-1">
                <div className="d-flex align-items-start" style={{ gap: 8 }}>
                  {isMobile && (
                    <button
                      type="button"
                      className="d-flex align-items-center justify-content-center border-0 rounded-lg flex-shrink-0"
                      style={{
                        width: 36,
                        height: 36,
                        color: '#334155',
                        background: '#f1f5f9'
                      }}
                      onClick={() => setMobileSidebarOpen(true)}
                      aria-label="Open sidebar"
                    >
                      <Menu size={18} />
                    </button>
                  )}
                  <div className={`min-w-0 ${isMobile ? 'ml-1 mb-3' : ''}`}>
                    <h6 className="font-weight-bold xxxlargeFont mb-1">{heading.title}</h6>
                    <p className="text-muted midFont mb-0">
                      {activeTabItemsMetadata?.loading ? (
                        <TextSkeleton lines={1} lineWidths={[40]} />
                      ) : (
                        `${activeTabItemsMetadata?.count ?? 0} items`
                      )}
                    </p>
                  </div>
                </div>
              </div>
              {QA_RULES_TAB_IDS.has(activeItemId) && (
                <Button
                  variant="info"
                  className="d-flex align-items-center font-weight-bold flex-shrink-0"
                  style={{ gap: 6, fontSize: 13 }}
                  disabled={activeTabItemsMetadata?.saveDisabled !== false}
                  onClick={() => void qaRulesRef.current?.save?.()}
                >
                  {activeTabItemsMetadata?.saving ? <Spinner animation="border" size="sm" /> : <Save size={14} />}
                  {activeTabItemsMetadata?.saving ? 'Saving...' : 'Save'}
                </Button>
              )}
              {showLocationSelect && (
                <div className="d-flex align-items-center flex-shrink-0" style={{ width: isMobile ? '100%' : 'auto' }}>
                  <Select
                    classNamePrefix="select"
                    className={`basic-single select-organization ${isMobile ? 'mx-4' : ''}`}
                    placeholder={locationSelectPlaceholder}
                    options={locationSelectOptions}
                    value={locationSelectValue}
                    onChange={handleLocationSelect}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    isClearable
                    styles={{
                      container: base => ({
                        ...base,
                        width: isMobile ? '100%' : 'min(300px, 100vw - 2rem)',
                      }),
                      control: base => ({
                        ...base,
                        minHeight: '40px',
                        height: '40px'
                      }),
                      menu: base => ({
                        ...base,
                        zIndex: 1060
                      }),
                      menuPortal: base => ({
                        ...base,
                        zIndex: 1060
                      })
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div style={{ flex: '1 1 auto', minWidth: 0, width: '100%' }} key={activeItemId}>
            {panel}
          </div>
        </>
      </BackOfficeSidebar>
      <CountyMunicipalityModal
        show={countyMunicipalityModalOpen}
        onHide={() => setCountyMunicipalityModalOpen(false)}
      />
    </>
  );
};

export default BackOffice;
